// React Hook for Resource Budget Settings

import { useState, useEffect, useCallback } from 'react';
import {
  getResourceBudgetSettings,
  saveResourceBudgetSettings,
  calculateBudgetSummary,
} from '@/lib/resource-budget-settings-storage';
import type {
  ResourceBudgetSettings,
  ResourceBudgetSummary,
} from '@/types/resource-budget-settings.types';

export function useResourceBudgetSettings() {
  const [settings, setSettings] = useState<ResourceBudgetSettings>(
    getResourceBudgetSettings()
  );
  const [summary, setSummary] = useState<ResourceBudgetSummary>(
    calculateBudgetSummary(getResourceBudgetSettings())
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    const handleStorageChange = () => {
      const newSettings = getResourceBudgetSettings();
      setSettings(newSettings);
      setSummary(calculateBudgetSummary(newSettings));
      setHasUnsavedChanges(false);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const updateSettings = useCallback(
    (updates: Partial<ResourceBudgetSettings>) => {
      const newSettings = { ...settings, ...updates };
      setSettings(newSettings);
      setSummary(calculateBudgetSummary(newSettings));
      setHasUnsavedChanges(true);
    },
    [settings]
  );

  const saveSettings = useCallback(() => {
    saveResourceBudgetSettings(settings);
    setHasUnsavedChanges(false);
  }, [settings]);

  const discardChanges = useCallback(() => {
    const currentSettings = getResourceBudgetSettings();
    setSettings(currentSettings);
    setSummary(calculateBudgetSummary(currentSettings));
    setHasUnsavedChanges(false);
  }, []);

  return {
    settings,
    summary,
    hasUnsavedChanges,
    updateSettings,
    saveSettings,
    discardChanges,
  };
}
